import { 
  Transaction, 
  Account, 
  Category, 
  Schedule, 
  BudgetAllocation,
  DailyAvailableBreakdown 
} from '../types';
import { schedulingEngine } from '../scheduler/engine';

export interface DailyAvailableSettings {
  includeGoalsInDA: boolean;
  useOnlyVariableCategories: boolean;
  roundDownToWhole: boolean;
}

export class DailyAvailableCalculator {
  private static instance: DailyAvailableCalculator;
  
  private constructor() {}
  
  static getInstance(): DailyAvailableCalculator {
    if (!DailyAvailableCalculator.instance) {
      DailyAvailableCalculator.instance = new DailyAvailableCalculator();
    }
    return DailyAvailableCalculator.instance;
  }

  // Get the current budget period (month) boundaries
  private getBudgetPeriod(currentMonth: string): { start: string; end: string } {
    const [year, month] = currentMonth.split('-').map(Number);
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0); // Last day of month
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  }

  // Calculate days remaining in budget period (inclusive of today)
  private getDaysRemaining(periodEnd: string, today: string = new Date().toISOString().split('T')[0]): number {
    const todayDate = new Date(today);
    const endDate = new Date(periodEnd);
    
    const diffTime = endDate.getTime() - todayDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include today
    
    return Math.max(1, diffDays); // Always at least 1 day
  }

  // Get recurring income due from today to period end
  private getRecurringIncomeRemaining(
    schedules: Schedule[],
    transactions: Transaction[],
    fromDate: string,
    toDate: string
  ): number {
    let total = 0;
    
    const incomeSchedules = schedules.filter(s => s.active && s.type === 'income');
    
    incomeSchedules.forEach(schedule => {
      const dueInstances = schedulingEngine.getDueInstances(schedule, fromDate, toDate);
      
      dueInstances.forEach(dueDate => {
        // Check if this instance hasn't been posted yet
        const alreadyPosted = transactions.some(tx => 
          tx.scheduleId === schedule.id && tx.scheduleDueDate === dueDate
        );
        
        if (!alreadyPosted) {
          total += schedule.template.amount;
        }
      });
    });
    
    return total;
  }

  // Get recurring expenses due from today to period end
  private getRecurringExpensesRemaining(
    schedules: Schedule[],
    transactions: Transaction[],
    fromDate: string,
    toDate: string
  ): number {
    let total = 0;
    
    const expenseSchedules = schedules.filter(s => s.active && s.type === 'expense');
    
    expenseSchedules.forEach(schedule => {
      const dueInstances = schedulingEngine.getDueInstances(schedule, fromDate, toDate);
      
      dueInstances.forEach(dueDate => {
        // Check if this instance hasn't been posted yet
        const alreadyPosted = transactions.some(tx => 
          tx.scheduleId === schedule.id && tx.scheduleDueDate === dueDate
        );
        
        if (!alreadyPosted) {
          total += schedule.template.amount;
        }
      });
    });
    
    return total;
  }

  // Get remaining cash from liquid accounts earmarked for this period's variable spend
  private getRemainingCash(
    accounts: Account[],
    budgetAllocations: BudgetAllocation[],
    categories: Category[],
    currentMonth: string,
    settings: DailyAvailableSettings
  ): number {
    // Calculate unspent variable envelopes
    let unspentEnvelopes = 0;
    
    const monthAllocations = budgetAllocations.filter(ba => ba.month === currentMonth);
    
    monthAllocations.forEach(allocation => {
      const category = categories.find(c => c.id === allocation.categoryId);
      
      if (!category) return;
      
      // Only include variable categories if setting enabled
      if (settings.useOnlyVariableCategories && !category.envelope) return;
      
      // Skip goals/sinking funds if setting disabled
      if (!settings.includeGoalsInDA && category.name.toLowerCase().includes('fund')) return;
      if (!settings.includeGoalsInDA && category.group === 'Debt & Savings') return;
      
      const remaining = allocation.allocated - Math.abs(allocation.spent);
      if (remaining > 0) {
        unspentEnvelopes += remaining;
      }
    });
    
    // Add liquid account balances (simplified - assuming all account balance is available)
    const liquidBalance = accounts
      .filter(acc => acc.status === 'open' && acc.type !== 'credit')
      .reduce((sum, acc) => sum + (acc.balance || 0), 0);
    
    return unspentEnvelopes + Math.max(0, liquidBalance);
  }

  // Main calculation method
  calculate(
    accounts: Account[],
    transactions: Transaction[],
    categories: Category[],
    budgetAllocations: BudgetAllocation[],
    schedules: Schedule[],
    currentMonth: string,
    settings: DailyAvailableSettings,
    today: string = new Date().toISOString().split('T')[0]
  ): DailyAvailableBreakdown {
    const period = this.getBudgetPeriod(currentMonth);
    const daysRemaining = this.getDaysRemaining(period.end, today);
    
    // Calculate components
    const incomeRemaining = this.getRecurringIncomeRemaining(schedules, transactions, today, period.end);
    const fixedExpensesRemaining = this.getRecurringExpensesRemaining(schedules, transactions, today, period.end);
    const variablePoolRemaining = this.getRemainingCash(accounts, budgetAllocations, categories, currentMonth, settings);
    
    // Calculate discretionary pool
    const discretionaryPool = Math.max(0, 
      incomeRemaining + variablePoolRemaining - fixedExpensesRemaining
    );
    
    // Calculate daily available
    let dailyAvailable = discretionaryPool / daysRemaining;
    
    if (settings.roundDownToWhole) {
      dailyAvailable = Math.floor(dailyAvailable);
    }
    
    dailyAvailable = Math.max(0, dailyAvailable);
    
    return {
      incomeRemaining,
      fixedExpensesRemaining,
      variablePoolRemaining,
      daysRemaining,
      discretionaryPool,
      dailyAvailable
    };
  }

  // Helper method to format currency for display
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  // Get a human-readable summary of the calculation
  getSummary(breakdown: DailyAvailableBreakdown, settings: DailyAvailableSettings): string {
    const { incomeRemaining, fixedExpensesRemaining, variablePoolRemaining, daysRemaining, dailyAvailable } = breakdown;
    
    const parts = [];
    
    if (incomeRemaining > 0) {
      parts.push(`${this.formatCurrency(incomeRemaining)} income remaining`);
    }
    
    if (variablePoolRemaining > 0) {
      parts.push(`${this.formatCurrency(variablePoolRemaining)} available now`);
    }
    
    if (fixedExpensesRemaining > 0) {
      parts.push(`${this.formatCurrency(fixedExpensesRemaining)} in upcoming fixed expenses`);
    }
    
    const daysText = daysRemaining === 1 ? '1 day' : `${daysRemaining} days`;
    
    if (parts.length === 0) {
      return `No funds available for the remaining ${daysText}.`;
    }
    
    return `${parts.join(', ')}, spread over the remaining ${daysText}.`;
  }
}

export const dailyAvailableCalculator = DailyAvailableCalculator.getInstance();